import { useRouteLocale } from "@/hooks/useRouteLocale";
import { useCallback, useEffect, useMemo } from "react";
import { toast } from "sonner";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useTranslation } from "@/hooks/useTranslation";
import { useAuth } from "@/hooks/useAuth";
import { useLoginStore, type LoginStore } from "@/stores/loginStore";
import { LoginFormFields } from "@/components/login/LoginFormFields";
import { LoginErrorMessage } from "@/components/login/LoginErrorMessage";
import { Button } from "@/components/ui/button";
import { ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import { LanguageSwitcher } from "@/components/common/LanguageSwitcher";
import { PUBLIC_ROUTES } from "@/routes";
import { usePageMeta } from "@/hooks/usePageMeta";
import { AUTH_REDIRECT_REASON_KEY, AUTH_REDIRECT_REASON_SESSION_EXPIRED } from "@/services/api/client";

type LoginFormData = {
  user_email: string;
  user_password: string;
};

export default function SuperadminLoginPage() {
  const { t } = useTranslation();
  const locale = useRouteLocale();
  const currentLocale = locale || "ro";
  usePageMeta({
    title: t("auth.superadminMetaTitle"),
    description: t("auth.superadminSignInHint"),
    locale: currentLocale,
    path: PUBLIC_ROUTES.superadminLogin(currentLocale),
    noindex: true,
  });
  const { loginSuperadmin, loading } = useAuth();
  const loginError = useLoginStore((state: LoginStore) => state.error);
  const setLoginError = useLoginStore((state: LoginStore) => state.setError);
  const clearLoginError = useLoginStore((state: LoginStore) => state.clearError);

  useEffect(() => {
    if (sessionStorage.getItem(AUTH_REDIRECT_REASON_KEY) === AUTH_REDIRECT_REASON_SESSION_EXPIRED) {
      sessionStorage.removeItem(AUTH_REDIRECT_REASON_KEY);
      const message = t("auth.sessionExpired");
      setLoginError(message);
      setTimeout(() => toast.error(message), 0);
    }
  }, [t, setLoginError]);

  const loginSchema = useMemo(
    () =>
      z.object({
        user_email: z.string().email(t("validation.emailInvalid")),
        user_password: z.string().min(1, t("validation.passwordRequired")),
      }),
    [t]
  );

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
  });

  const onSubmit = useCallback(
    async (data: LoginFormData) => {
      try {
        await loginSuperadmin(data.user_email, data.user_password);
        clearLoginError();
      } catch (error: unknown) {
        const err = error as {
          response?: { data?: { error?: { message_key?: string; code?: string; message?: string } } };
          message?: string;
        };
        const errObj = err.response?.data?.error;
        const errorKey = errObj?.message_key || errObj?.code || "invalid_credentials";
        const translated = t(`errors.${errorKey}`);
        const errorMessage =
          translated && translated !== `errors.${errorKey}`
            ? translated
            : errObj?.message || t("auth.invalidCredentials");
        setLoginError(errorMessage);
      }
    },
    [loginSuperadmin, clearLoginError, setLoginError, t]
  );

  const onFocus = useCallback(() => {
    clearLoginError();
  }, [clearLoginError]);

  return (
    <div className="min-h-screen bg-background">
      <header className="sticky top-0 z-50 w-full border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
        <div className="container mx-auto px-4">
          <div className="flex h-16 items-center justify-between">
            <Link to={`/${currentLocale}`} className="flex items-center flex-shrink-0 overflow-visible">
              <span
                className="font-bold font-logo bg-gradient-to-r from-pink-500 to-rose-500 bg-clip-text text-transparent whitespace-nowrap overflow-visible"
                style={{ fontSize: "36px", lineHeight: "40px", display: "inline-block", paddingLeft: "5px", paddingRight: "0" }}
              >
                {t("header.logo")}
              </span>
            </Link>
            <LanguageSwitcher compact />
          </div>
        </div>
      </header>

      <div className="flex items-start justify-center min-h-[calc(100vh-4rem)] pt-20 pb-8 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-pink-50/50 to-background dark:from-pink-950/20 dark:to-background">
        <div className="w-full max-w-md space-y-6">
          <div className="bg-card border rounded-lg p-8 shadow-lg">
            <div className="space-y-6">
              <div className="text-center">
                <h1 className="text-xl font-semibold text-foreground">
                  {t("auth.superadminSignIn") ?? "Site administrator sign in"}
                </h1>
                <p className="text-sm text-muted-foreground mt-1">
                  {t("auth.superadminSignInHint") ?? "Only site administrators can sign in here."}
                </p>
              </div>

              <form
                className="space-y-5"
                onSubmit={(e) => {
                  e.preventDefault();
                  handleSubmit(onSubmit)(e).catch(() => {});
                }}
                noValidate
              >
                {loginError && (
                  <LoginErrorMessage error={loginError} checkCredentialsText={t("auth.checkCredentials")} />
                )}
                <LoginFormFields register={register} errors={errors} onFocus={onFocus} />
                <div className="flex justify-end">
                  <Button
                    type="submit"
                    className="w-full bg-gradient-to-r from-pink-500 to-rose-500 hover:from-pink-600 hover:to-rose-600 text-white shadow-lg shadow-pink-500/50"
                    disabled={isSubmitting || loading}
                    data-testid="superadmin-login-submit"
                  >
                    {isSubmitting || loading ? t("common.loading") : (
                      <>
                        {t("auth.signIn")}
                        <ArrowRight className="ml-2 h-4 w-4" />
                      </>
                    )}
                  </Button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
